import { averageSpeed, steamingHours } from './calc.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Seyir incelemesi COSP → EOSP (ADR 0058 §6; ortağın `g4.jpeg` §2 COSP referansı, §4 seyir
 * performansı, §6 CP performansı): EOSP formunda gösterilir, saklanmaz. Brüt değerlerdir: seyir
 * içindeki demir, drift ve boğaz süreleri düşülmez; CP penceresinin resmî hesabı Charter Party
 * ekranındadır (ADR 0022 §4). Süre ve hız aynı formüllerle (`steamingHours`, `averageSpeed`).
 */

export interface ReviewReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  lat?: number | null;
  lon?: number | null;
  data: Readonly<Record<string, unknown>>;
}

export interface CpTermsInput {
  cpSpeedLadenKn?: number | null;
  cpSpeedBallastKn?: number | null;
  cpConsumptionLadenMt?: number | null;
  cpConsumptionBallastMt?: number | null;
}

export interface PassageReview {
  cosp: { id: string; reportedAt: string; lat: number | null; lon: number | null };
  hours: number | null;
  distanceNm: number;
  avgSpeedKn: number | null;
  fuels: {
    fuel: string;
    atCosp: number | null;
    atEosp: number | null;
    consumedMt: number | null;
  }[];
  freshWater: { atCosp: number | null; atEosp: number | null; consumedMt: number | null } | null;
  cp: {
    loadCondition: 'laden' | 'ballast';
    orderedSpeedKn: number | null;
    speedDiffKn: number | null;
    allowedMt: number | null;
    actualMt: number | null;
    diffMt: number | null;
    diffPct: number | null;
  } | null;
}

const round = (value: number, digits = 3) => Number(value.toFixed(digits));
const numberOf = (value: unknown) => (typeof value === 'number' ? value : null);
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

function robOf(data: Readonly<Record<string, unknown>>): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of Array.isArray(data.rob) ? data.rob : []) {
    const { fuel, rob } = record(row);
    if (typeof fuel === 'string' && typeof rob === 'number') out.set(fuel, rob);
  }
  return out;
}

const distanceOf = (report: { kind: string; data: Readonly<Record<string, unknown>> }) =>
  numberOf(
    report.kind === 'noon'
      ? record(report.data.navigation).distanceSinceLast
      : report.data.distanceSinceLast,
  ) ?? 0;

export function passageReview(
  reports: readonly ReviewReport[],
  eosp: { reportedAt: string; data: Readonly<Record<string, unknown>> },
  terms: CpTermsInput,
): PassageReview | null {
  const end = Date.parse(eosp.reportedAt);
  const sorted = [...reports].sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  const cosp = sorted.filter((r) => r.kind === 'cosp' && Date.parse(r.reportedAt) < end).at(-1);
  if (!cosp) return null;
  const start = Date.parse(cosp.reportedAt);
  const between = sorted.filter((r) => {
    const t = Date.parse(r.reportedAt);
    return t > start && t < end && r.kind !== 'eosp';
  });
  const distanceNm = round(
    between.reduce((sum, r) => sum + distanceOf(r), 0) +
      distanceOf({ kind: 'eosp', data: eosp.data }),
  );
  const hours = steamingHours(cosp.reportedAt, eosp.reportedAt);
  const atCosp = robOf(cosp.data);
  const atEosp = robOf(eosp.data);
  const fuels = [...new Set([...atCosp.keys(), ...atEosp.keys()])].map((fuel) => {
    const a = atCosp.get(fuel) ?? null;
    const b = atEosp.get(fuel) ?? null;
    return {
      fuel,
      atCosp: a,
      atEosp: b,
      consumedMt: a !== null && b !== null ? round(a - b) : null,
    };
  });
  const waterA = numberOf(record(cosp.data.freshWater).rob);
  const waterB = numberOf(record(eosp.data.freshWater).rob);
  const freshWater =
    waterA === null && waterB === null
      ? null
      : {
          atCosp: waterA,
          atEosp: waterB,
          consumedMt: waterA !== null && waterB !== null ? round(waterA - waterB) : null,
        };
  const avgSpeedKn = averageSpeed(distanceNm, hours);
  const load = cosp.data.loadCondition;
  const laden = load === 'laden';
  const ordered = laden ? terms.cpSpeedLadenKn : terms.cpSpeedBallastKn;
  const daily = laden ? terms.cpConsumptionLadenMt : terms.cpConsumptionBallastMt;
  const consumed = fuels.map((f) => f.consumedMt);
  const actualMt =
    consumed.length > 0 && consumed.every((c) => c !== null)
      ? round((consumed as number[]).reduce((a, b) => a + b, 0))
      : null;
  const allowedMt = daily != null && hours !== null ? round((daily * hours) / 24) : null;
  const diffMt = allowedMt !== null && actualMt !== null ? round(actualMt - allowedMt) : null;
  const cp =
    (load === 'laden' || load === 'ballast') && (ordered != null || daily != null)
      ? {
          loadCondition: load as 'laden' | 'ballast',
          orderedSpeedKn: ordered ?? null,
          speedDiffKn: ordered != null && avgSpeedKn !== null ? round(avgSpeedKn - ordered) : null,
          allowedMt,
          actualMt,
          diffMt,
          diffPct: diffMt !== null && allowedMt ? round((diffMt / allowedMt) * 100, 1) : null,
        }
      : null;
  return {
    cosp: {
      id: cosp.id,
      reportedAt: cosp.reportedAt,
      lat: cosp.lat ?? null,
      lon: cosp.lon ?? null,
    },
    hours,
    distanceNm,
    avgSpeedKn,
    fuels,
    freshWater,
    cp,
  };
}
